/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { FiRefreshCcw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequestBodyBuilder } from "../config/tableHelper";

export default function CampaignTable({ groupType, setRefreshCounter }) {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const headerCheckboxRef = useRef(null);

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      let requestBody = apiRequestBodyBuilder();
      requestBody.type = groupType === "keyword" ? "keywords" : null;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      const result = await response.json();
      setData(result.groups || []);
    } catch (error) {
      console.error("Error fetching data: ", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [groupType]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      const numSelected = selectedGroupIds.length;
      const numItems = data.length;
      headerCheckboxRef.current.checked =
        numSelected === numItems && numItems > 0;
      headerCheckboxRef.current.indeterminate =
        numSelected > 0 && numSelected < numItems;
    }
  }, [selectedGroupIds, data]);

  const handleDelete = async (ids) => {
    if (!ids || ids.length === 0) return;

    const confirmationMessage =
      ids.length > 1
        ? `Are you sure you want to delete these ${ids.length} groups?`
        : "Are you sure you want to delete this group?";

    if (!window.confirm(confirmationMessage)) return;

    try {
      // UPDATED: Constructed payload matches new delete API requirements
      let ReqBody = apiRequestBodyBuilder(null, {
        type: `${groupType}_group`, // e.g., "campaign_group" or "keyword_group"
        group_ids: ids,
      });

      // UPDATED: New API Endpoint for Delete
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/delete",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ReqBody),
        },
      );

      const result = await response.json();
      if (response.ok) {
        alert("Group(s) deleted successfully!");
        setSelectedGroupIds([]);
        fetchData();
      } else {
        alert(
          `Failed to delete group(s): ${result.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Delete error: ", error);
      alert("Error deleting group(s). Please try again.");
    } finally {
      setRefreshCounter(Math.random());
    }
  };

  let formattedGroupType = groupType
    ? groupType?.charAt(0).toUpperCase() + groupType?.slice(1)
    : "";

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 ">
          📂 {formattedGroupType} Groups
        </h2>
        <div className="flex items-center gap-3 text-sm font-medium">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-1.5 hover:text-blue-800 text-blue-600  transition"
          >
            <FiRefreshCcw size={16} />
            Refresh
          </button>
          <button
            onClick={() => handleDelete(data.map((d) => d.group_id))}
            className="flex items-center gap-2 px-3 py-1.5  text-red-600  hover:text-red-800 transition"
          >
            <Trash2 size={18} />
            Clear All
          </button>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="flex justify-center items-center h-48 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mr-3"></div>
          <span>Loading data...</span>
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <p className="italic text-base">No saved groups found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {data.map((item) => (
            <div
              key={item.group_id}
              className="relative bg-gray-50 border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 group"
            >
              {/* Card Header */}
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 break-words">
                  {item.group_name || "—"}
                </h3>
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() =>
                      navigate(
                        `/google-ads/editgroup?groupId=${item.group_id}&groupType=${groupType}`,
                      )
                    }
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit Group"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete([item.group_id])}
                    className="text-red-600 hover:text-red-800"
                    title="Delete Group"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="text-sm text-gray-500">
                <p>
                  <span className="font-medium text-gray-700">Group ID:</span>{" "}
                  {item.group_id}
                </p>
                {item.created_at && (
                  <p className="mt-1 text-gray-400 text-xs">
                    Created: {new Date(item.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
